// ============================================================================
// Beacon — portfolio growth simulator ("the snowball").
//
// Seeds from your current portfolio's equity + cash flow, then models a
// repeatable acquisition engine: each year, cash flow + refinance proceeds pool
// up; whenever the pool covers a down payment you buy another property; each new
// property then amortizes, appreciates, gets its rents bumped, and eventually
// refinances to fund the NEXT one. This is how one property becomes many.
//
// Pure & deterministic. Illustrative — every lever is an explicit assumption.
// ============================================================================

import type { Deal } from "../types";
import { calc, mortgageConstant, loanBalance } from "../engine/engine";
import { project } from "./projection";

export interface DealTemplate {
  price: number;       // price of a "typical" next acquisition
  downPct: number;     // down payment fraction (e.g. 0.25)
  capRate: number;     // going-in cap rate on price
  growth: number;      // annual rent/value growth
  valueAddPct: number; // one-time value lift realized by year 1 (renovate + raise rents)
  rate: number;        // mortgage rate
  amort: number;       // amortization years
  refiLTV: number;     // LTV a property refinances to
  refiAge: number;     // years until each property refinances once
  startCapital: number;// cash on hand to deploy at year 0
  maxPerYear: number;  // acquisitions cap per year (discipline)
}

export interface GrowthYear {
  year: number;
  newProps: number;      // cumulative NEW acquisitions
  totalProps: number;    // existing + new
  valueNew: number;
  equityNew: number;
  existingEquity: number;
  netWorth: number;      // existing equity + new equity
  cashFlow: number;      // annual, existing + new
  pool: number;          // cash left after buying
  bought: number;        // acquisitions this year
}

export interface GrowthResult {
  years: GrowthYear[];
  template: DealTemplate;
  endProps: number;
  end: GrowthYear;
}

const CAP = 400; // hard guard against pathological runaway loops

/** State of one templated property at a given age (with a one-time refi at refiAge). */
function propState(t: DealTemplate, age: number) {
  const k = mortgageConstant(t.rate, t.amort);
  const value = t.price * Math.pow(1 + t.growth, age) * (1 + (age >= 1 ? t.valueAddPct : 0));
  const initialLoan = t.price * (1 - t.downPct);
  const noi = value * t.capRate;
  let balance: number, debtSvc: number, refiCash = 0;
  if (age < t.refiAge) {
    balance = loanBalance(initialLoan, t.rate, t.amort, age);
    debtSvc = initialLoan * k;
  } else {
    const valAtRefi = t.price * Math.pow(1 + t.growth, t.refiAge) * (1 + t.valueAddPct);
    const refiLoan = t.refiLTV * valAtRefi;
    if (age === t.refiAge) refiCash = Math.max(0, refiLoan - loanBalance(initialLoan, t.rate, t.amort, t.refiAge));
    balance = loanBalance(refiLoan, t.rate, t.amort, age - t.refiAge);
    debtSvc = refiLoan * k;
  }
  return { value, balance, equity: value - balance, cashFlow: noi - debtSvc, refiCash };
}

export function simulateGrowth(deals: Deal[], t: DealTemplate, horizon = 30): GrowthResult {
  const metrics = deals.map((d) => calc(d));
  const projs = deals.map((d, i) => project(d, metrics[i], horizon, 0));
  const existEq = (y: number) => (y === 0 ? projs.reduce((s, p) => s + p.e0, 0) : projs.reduce((s, p) => s + p.rows[y - 1].equity, 0));
  const existCF = (y: number) => (y === 0 ? metrics.reduce((s, m) => s + m.cashFlow, 0) : projs.reduce((s, p) => s + p.rows[y - 1].cashFlow, 0));

  const down = t.price * t.downPct;
  const acq: number[] = []; // acquisition years
  let pool = t.startCapital;
  const years: GrowthYear[] = [];

  for (let y = 0; y <= horizon; y++) {
    // income from properties owned BEFORE this year (age ≥ 1) + refis reaching this year
    if (y > 0) {
      let income = existCF(y);
      for (const ay of acq) {
        const age = y - ay;
        if (age >= 1) {
          const st = propState(t, age);
          income += st.cashFlow + st.refiCash;
        }
      }
      pool += income;
    }
    // deploy: buy while the pool covers a down payment
    let bought = 0;
    while (pool >= down && bought < t.maxPerYear && acq.length < CAP) {
      acq.push(y);
      pool -= down;
      bought++;
    }
    // aggregate the book at year y
    let valueNew = 0, equityNew = 0, cfNew = 0;
    for (const ay of acq) {
      const st = propState(t, y - ay);
      valueNew += st.value;
      equityNew += st.equity;
      cfNew += st.cashFlow;
    }
    const ee = existEq(y);
    years.push({
      year: y, newProps: acq.length, totalProps: deals.length + acq.length,
      valueNew, equityNew, existingEquity: ee, netWorth: ee + equityNew,
      cashFlow: existCF(y) + cfNew, pool, bought,
    });
  }

  return { years, template: t, endProps: deals.length + acq.length, end: years[years.length - 1] };
}

// ---------------------------------------------------------------------------
// Goal seek — "I want $X/mo passive income by year N; what's the plan?"
// ---------------------------------------------------------------------------
export type GoalLever = "valueAddPct" | "maxPerYear" | "startCapital";

export interface GoalResult {
  targetAnnual: number;
  byYear: number;
  currentHitYear: number | null;       // when the CURRENT plan first reaches the target
  currentAtGoalYear: number;           // cash flow at byYear under current settings
  achievable: boolean;
  lever: GoalLever;
  neededValue: number;                 // the min lever value that hits the goal by byYear
}

/** First year the portfolio's annual cash flow reaches `targetAnnual`, else null. */
export function firstYearAtIncome(r: GrowthResult, targetAnnual: number): number | null {
  const y = r.years.find((v) => v.cashFlow >= targetAnnual);
  return y ? y.year : null;
}

const LEVER_RANGE: Record<GoalLever, [number, number, number]> = {
  valueAddPct: [0, 0.4, 0.005],
  maxPerYear: [1, 8, 1],
  startCapital: [0, 3_000_000, 25_000],
};

export function solveGoal(deals: Deal[], t: DealTemplate, horizon: number, targetMonthly: number, byYear: number, lever: GoalLever): GoalResult {
  const targetAnnual = targetMonthly * 12;
  const yr = Math.min(byYear, horizon);
  const cur = simulateGrowth(deals, t, horizon);
  const hits = (v: number) => (simulateGrowth(deals, { ...t, [lever]: v }, horizon).years[yr]?.cashFlow ?? 0) >= targetAnnual;

  const [lo, hi, step] = LEVER_RANGE[lever];
  let needed = hi;
  let achievable = false;
  for (let v = lo; v <= hi + 1e-9; v += step) {
    if (hits(v)) { needed = +v.toFixed(6); achievable = true; break; }
  }
  return {
    targetAnnual, byYear: yr,
    currentHitYear: firstYearAtIncome(cur, targetAnnual),
    currentAtGoalYear: cur.years[yr]?.cashFlow ?? 0,
    achievable, lever, neededValue: needed,
  };
}
